import React,{useEffect,useMemo,useRef,useState} from 'react';
import {useNavigate,useSearchParams} from 'react-router-dom';
import {motion,AnimatePresence} from 'framer-motion';
import {useSupabaseData} from '../contexts/SupabaseDataContext';
import {useSupabaseAuth} from '../contexts/SupabaseAuthContext';
import {format} from 'date-fns';
import {filterAndSortOrders,ORDER_SORT_OPTIONS} from '../services/orderQueryService';
import PickupConfirmationPanel from '../components/orders/PickupConfirmationPanel';
import AutoConfirmationTimer from '../components/orders/AutoConfirmationTimer';
import BuyerDeliveryConfirmationButton from '../components/orders/BuyerDeliveryConfirmationButton';
import DeliveryIssueReportButton from '../components/orders/DeliveryIssueReportButton';
import SafeIcon from '../common/SafeIcon';
import OrderTimeline from '../components/orders/OrderTimeline';
import ReviewModal from '../components/reviews/ReviewModal';
import InvoiceDocument from '../components/orders/InvoiceDocument';
import OrderCustomizationSummary from '../components/orders/OrderCustomizationSummary';
import OrderPriceBreakdown from '../components/orders/OrderPriceBreakdown';
import AllergenLabels from '../components/common/AllergenLabels';
import IngredientDisclosure from '../components/common/IngredientDisclosure';
import * as FiIcons from 'react-icons/fi';

const {
  FiShoppingBag,
  FiMessageCircle,
  FiChevronDown,
  FiStar,
  FiShoppingCart,
  FiPackage,
  FiCalendar,
  FiTruck,
  FiMapPin,
  FiXCircle,
  FiLoader,
  FiFileText,
  FiSearch,
  FiFilter,
  FiArrowUpDown,
  FiCheck,
  FiX,
  FiCheckCircle,
  FiAlertCircle,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight
}=FiIcons;

const STATUSES=[
  'all',
  'pending',
  'accepted',
  'preparing',
  'ready',
  'completed',
  'cancelled'
];
const ORDERS_PER_PAGE=10;

const formatPrice=(price)=>{
  if (price===undefined || price===null) return '$0.00';
  const value=typeof price==='string' ? price.replace('$','') : price;
  return `$${parseFloat(value).toFixed(2)}`;
};

const getStatusColor=(status)=>{
  const colors={
    pending:'text-amber-600 bg-amber-50 border-amber-100',
    accepted:'text-indigo-600 bg-indigo-50 border-indigo-100',
    preparing:'text-purple-600 bg-purple-50 border-purple-100',
    ready:'text-emerald-600 bg-emerald-50 border-emerald-100',
    completed:'text-green-600 bg-green-50 border-green-100',
    cancelled:'text-red-600 bg-red-50 border-red-100',
    declined:'text-red-600 bg-red-50 border-red-100'
  };

  return colors[status] || 'text-slate-600 bg-slate-50 border-slate-100';
};

const getProcessingMessage=(status)=>{
  const messages={
    accepted:'The seller accepted your order. We are syncing the updated order details now.',
    preparing:'The seller started preparing your order. We are syncing the latest status now.',
    ready:'Your order is ready for handoff. We are syncing the latest fulfillment details now.'
  };

  return messages[status];
};

const BuyerStatusSyncNotice=({order})=>{
  const [visible,setVisible]=useState(false);

  useEffect(()=>{
    if (!order?.updated_at || !getProcessingMessage(order.status)) {
      setVisible(false);
      return undefined;
    }

    const updatedAt=new Date(order.updated_at).getTime();
    const age=Date.now()-updatedAt;

    if (age<15000) {
      setVisible(true);
      const timer=window.setTimeout(()=>setVisible(false),15000-age);
      return ()=>window.clearTimeout(timer);
    }

    setVisible(false);
    return undefined;
  },[order?.id,order?.status,order?.updated_at]);

  if (!visible || !getProcessingMessage(order.status)) return null;

  return(
    <div className="mb-6 flex items-start gap-3 rounded-2xl border border-indigo-100 bg-indigo-50 p-4 text-indigo-800">
      <SafeIcon icon={FiRefreshCw} className="mt-0.5 flex-shrink-0 animate-spin text-indigo-600" />
      <div>
        <p className="text-[10px] font-black uppercase tracking-widest">
          Order update in progress
        </p>
        <p className="mt-1 text-sm font-medium leading-relaxed">
          {getProcessingMessage(order.status)}
        </p>
      </div>
    </div>
  );
};

const SellerOrderActions=({order,onStatusUpdate,isUpdating})=>{
  if (!order || !['pending','accepted','preparing','ready'].includes(order.status)) {
    return null;
  }

  const actionLabels={
    accepted:'Accept Order',
    preparing:'Start Preparing',
    ready:'Mark Ready'
  };

  return(
    <div className="flex flex-wrap gap-3 w-full sm:w-auto">
      {order.status==='pending' && (
        <>
          <button
            type="button"
            onClick={(event)=>onStatusUpdate(event,order.id,'accepted')}
            disabled={isUpdating}
            className="flex items-center justify-center rounded-xl bg-indigo-600 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white disabled:cursor-wait disabled:opacity-70"
          >
            {isUpdating ? (
              <>
                <SafeIcon icon={FiLoader} className="mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <SafeIcon icon={FiCheck} className="mr-2" />
                Accept Order
              </>
            )}
          </button>

          <button
            type="button"
            onClick={(event)=>onStatusUpdate(event,order.id,'declined')}
            disabled={isUpdating}
            className="flex items-center justify-center rounded-xl border border-red-100 bg-white px-5 py-3 text-[10px] font-black uppercase tracking-widest text-red-600 disabled:cursor-wait disabled:opacity-70"
          >
            <SafeIcon icon={FiX} className="mr-2" />
            Decline
          </button>
        </>
      )}

      {order.status==='accepted' && (
        <button
          type="button"
          onClick={(event)=>onStatusUpdate(event,order.id,'preparing')}
          disabled={isUpdating}
          className="flex items-center rounded-xl bg-purple-600 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white disabled:cursor-wait disabled:opacity-70"
        >
          {isUpdating ? (
            <>
              <SafeIcon icon={FiLoader} className="mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            actionLabels.preparing
          )}
        </button>
      )}

      {order.status==='preparing' && (
        <button
          type="button"
          onClick={(event)=>onStatusUpdate(event,order.id,'ready')}
          disabled={isUpdating}
          className="flex items-center rounded-xl bg-indigo-500 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white disabled:cursor-wait disabled:opacity-70"
        >
          {isUpdating ? (
            <>
              <SafeIcon icon={FiLoader} className="mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            actionLabels.ready
          )}
        </button>
      )}
    </div>
  );
};

const OrderCard=({
  order,
  isSelling,
  expanded,
  onToggle,
  onCancel,
  cancellingId,
  onReview,
  onPrint,
  onMessage,
  onStatusUpdate,
  updatingOrderId,
  onPickupConfirmed,
  onDeliveryConfirmed,
  onAutoConfirmed,
  onDisputeReported
})=>(
  <motion.div
    className="overflow-hidden rounded-[2rem] border border-slate-100 bg-white shadow-xl shadow-slate-200/30"
  >
    <div
      className="cursor-pointer p-8 transition-colors hover:bg-slate-50/50"
      onClick={onToggle}
    >
      <div className="flex items-start justify-between gap-6">
        <div className="flex min-w-0 space-x-6">
          <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-[1.5rem] border border-slate-100 bg-slate-50">
            {order.items?.[0]?.image ? (
              <img
                src={order.items[0].image}
                className="h-full w-full object-cover"
                alt=""
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-slate-300">
                <SafeIcon icon={FiShoppingBag} className="text-2xl" />
              </div>
            )}
          </div>

          <div className="min-w-0">
            <h3 className="mb-2 truncate text-xl font-black tracking-tight text-slate-900">
              {order.items?.length>1
                ? `${order.items[0].title} + ${order.items.length-1} more`
                : order.items?.[0]?.title || 'Custom Order'}
            </h3>

            <p className="truncate text-xs font-bold uppercase tracking-widest text-slate-400">
              {isSelling
                ? `Customer: ${order.buyer?.name || 'User'}`
                : `Seller: ${order.seller?.business_name || 'Seller'}`}
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className={`rounded-full border px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] ${getStatusColor(order.status)}`}>
                {order.status}
              </span>

              <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                {format(new Date(order.created_at),'MMM d,yyyy')}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-shrink-0 flex-col items-end">
          <span className="text-2xl font-black tracking-tighter text-slate-900">
            {formatPrice(order.total_amount)}
          </span>

          <div className={`mt-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-50 text-xs text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`}>
            <SafeIcon icon={FiChevronDown} />
          </div>
        </div>
      </div>
    </div>

    <AnimatePresence>
      {expanded && (
        <motion.div
          initial={{height:0,opacity:0}}
          animate={{height:'auto',opacity:1}}
          exit={{height:0,opacity:0}}
          className="border-t border-slate-50 bg-slate-50/30 p-8 md:p-10"
        >
          {!isSelling && <BuyerStatusSyncNotice order={order} />}

          <OrderTimeline status={order.status} />

          <AutoConfirmationTimer
            order={order}
            onAutoConfirmed={onAutoConfirmed}
          />

          {order.status==='ready' && order.delivery_method==='pickup' && (
            <PickupConfirmationPanel
              order={order}
              role={isSelling ? 'seller' : 'buyer'}
              onConfirmed={onPickupConfirmed}
            />
          )}

          {!isSelling
            && order.status==='ready'
            && order.delivery_method==='delivery'
            && (
              <div className="mt-4 flex justify-end">
                <div className="flex flex-col items-end gap-3">
                  <BuyerDeliveryConfirmationButton
                    order={order}
                    onConfirmed={onDeliveryConfirmed}
                  />
                  <DeliveryIssueReportButton
                    order={order}
                    onReported={onDisputeReported}
                  />
                </div>
              </div>
            )}

          <div className="mt-10 space-y-4">
            <div className="mb-4 flex items-center justify-between">
              <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                Order Breakdown
              </h4>

              <button
                type="button"
                onClick={(event)=>{
                  event.stopPropagation();
                  onPrint(order);
                }}
                className="flex items-center text-[10px] font-black uppercase tracking-widest text-indigo-600"
              >
                <SafeIcon icon={FiFileText} className="mr-1.5" />
                View Invoice
              </button>
            </div>

            {(order.items || []).map((item,index)=>(
              <div
                key={`${order.id}-${index}`}
                className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div className="flex min-w-0 items-center">
                    <div className="mr-4 h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg bg-slate-50">
                      {item.image && (
                        <img
                          src={item.image}
                          className="h-full w-full object-cover"
                          alt=""
                        />
                      )}
                    </div>

                    <div className="min-w-0">
                      <p className="truncate text-sm font-black text-slate-900">
                        {item.title}
                      </p>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        Qty: {item.quantity}
                      </p>
                    </div>
                  </div>

                  <span className="ml-4 text-sm font-black text-slate-900">
                    {formatPrice(item.price)}
                  </span>
                </div>

                <OrderPriceBreakdown
                  item={item}
                  currency={order.stripe_currency || 'cad'}
                />
                <OrderCustomizationSummary item={item} />
                <IngredientDisclosure ingredients={item.ingredients} />
                <AllergenLabels allergens={item.allergens} />
              </div>
            ))}

            <div className="flex items-center justify-between rounded-2xl border border-slate-100 bg-white p-6 shadow-md">
              <span className="text-xs font-black uppercase tracking-widest text-slate-900">
                Total Transaction
              </span>

              <span className="text-2xl font-black text-indigo-600">
                {formatPrice(order.total_amount)}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-2">
              <div className="flex items-center rounded-2xl border border-slate-100 bg-white p-5">
                <SafeIcon icon={FiCalendar} className="mr-4 text-indigo-600" />
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                    Requested Fulfillment
                  </p>
                  <p className="font-bold text-slate-900">
                    {order.requested_date
                      ? format(new Date(order.requested_date),'PPP')
                      : 'N/A'}
                    {order.requested_time && ` at ${order.requested_time}`}
                  </p>
                </div>
              </div>

              <div className="flex items-center rounded-2xl border border-slate-100 bg-white p-5">
                <SafeIcon
                  icon={order.delivery_method==='delivery' ? FiTruck : FiMapPin}
                  className="mr-4 text-indigo-600"
                />
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                    Method
                  </p>
                  <p className="font-bold capitalize text-slate-900">
                    {order.delivery_method || 'Pickup'}
                  </p>
                </div>
              </div>
            </div>

            {isSelling && ['pending','accepted','preparing'].includes(order.status) && (
              <div className="flex justify-end pt-6">
                <SellerOrderActions
                  order={order}
                  onStatusUpdate={onStatusUpdate}
                  isUpdating={updatingOrderId===order.id}
                />
              </div>
            )}

            <div className="flex flex-col items-center justify-between gap-4 pt-8 sm:flex-row">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                ORDER ID: {order.id.slice(0,8).toUpperCase()}
              </span>

              <div className="flex flex-wrap justify-end gap-3">
                {!isSelling && order.status==='pending' && (
                  <button
                    type="button"
                    onClick={(event)=>{
                      event.stopPropagation();
                      onCancel(event,order.id);
                    }}
                    disabled={cancellingId===order.id}
                    className="flex items-center justify-center rounded-xl border border-red-100 bg-red-50 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-red-600"
                  >
                    {cancellingId===order.id ? (
                      <SafeIcon icon={FiLoader} className="animate-spin" />
                    ) : (
                      <>
                        <SafeIcon icon={FiXCircle} className="mr-2" />
                        Cancel
                      </>
                    )}
                  </button>
                )}

                {!isSelling && order.status==='completed' && !order.has_review && (
                  <button
                    type="button"
                    onClick={(event)=>{
                      event.stopPropagation();
                      onReview(order);
                    }}
                    className="flex items-center justify-center rounded-xl bg-indigo-600 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white"
                  >
                    <SafeIcon icon={FiStar} className="mr-2" />
                    Rate
                  </button>
                )}

                <button
                  type="button"
                  onClick={(event)=>{
                    event.stopPropagation();
                    onMessage(order);
                  }}
                  className="flex items-center justify-center rounded-xl bg-slate-900 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white"
                >
                  <SafeIcon icon={FiMessageCircle} className="mr-2 text-indigo-400" />
                  {isSelling ? 'Message Buyer' : 'Message Seller'}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const OrdersPage=()=>{
  const {user,profile}=useSupabaseAuth();
  const {
    orders,
    sellers,
    addReview,
    updateOrderStatus,
    refreshData,
    isDataReady
  }=useSupabaseData();
  const [searchParams]=useSearchParams();
  const navigate=useNavigate();
  const [filter,setFilter]=useState('all');
  const [sort,setSort]=useState('newest');
  const [search,setSearch]=useState('');
  const [hideCancelled,setHideCancelled]=useState(false);
  const [expandedOrder,setExpandedOrder]=useState(null);
  const [orderToReview,setOrderToReview]=useState(null);
  const [orderToPrint,setOrderToPrint]=useState(null);
  const [activeRoleTab,setActiveRoleTab]=useState('buying');
  const [cancellingId,setCancellingId]=useState(null);
  const [updatingOrderId,setUpdatingOrderId]=useState(null);
  const [showPaymentNotice,setShowPaymentNotice]=useState(true);
  const [preferencesLoadedFor,setPreferencesLoadedFor]=useState(null);
  const [page,setPage]=useState(1);
  const linkedOrderRef=useRef(null);

  const preferenceKey=user?.id
    ? `sohlocal:orders-view:${user.id}`
    : null;

  const isSeller=profile?.role==='seller' || profile?.is_seller;
  const currentSeller=sellers.find((seller)=>seller.user_id===user?.id);
  const linkedOrderId=searchParams.get('order');
  const paymentResult=searchParams.get('payment');
  const paymentOrderId=searchParams.get('order');

  useEffect(()=>{
    if (!preferenceKey) {
      setPreferencesLoadedFor(null);
      return;
    }

    let preferences={};

    try {
      preferences=JSON.parse(
        window.localStorage.getItem(preferenceKey) || '{}'
      );
    } catch {
      preferences={};
    }

    if (STATUSES.includes(preferences.filter)) {
      setFilter(preferences.filter);
    }
    if (ORDER_SORT_OPTIONS.some((option)=>option.value===preferences.sort)) {
      setSort(preferences.sort);
    }
    if (typeof preferences.search==='string') {
      setSearch(preferences.search);
    }
    if (typeof preferences.hideCancelled==='boolean') {
      setHideCancelled(preferences.hideCancelled);
    }
    if (
      preferences.expandedOrder===null
      || typeof preferences.expandedOrder==='string'
    ) {
      setExpandedOrder(preferences.expandedOrder);
    }
    if (['buying','selling'].includes(preferences.activeRoleTab)) {
      setActiveRoleTab(preferences.activeRoleTab);
    }

    setPreferencesLoadedFor(preferenceKey);
  },[preferenceKey]);

  useEffect(()=>{
    if (!preferenceKey || preferencesLoadedFor!==preferenceKey) return;

    try {
      window.localStorage.setItem(
        preferenceKey,
        JSON.stringify({
          filter,
          sort,
          search,
          hideCancelled,
          expandedOrder,
          activeRoleTab
        })
      );
    } catch {
      // Keep the orders page usable if browser storage is unavailable.
    }
  },[
    preferenceKey,
    preferencesLoadedFor,
    filter,
    sort,
    search,
    hideCancelled,
    expandedOrder,
    activeRoleTab
  ]);

  useEffect(()=>{
    if (!paymentResult || !user) return undefined;

    setShowPaymentNotice(true);
    refreshData();

    const refreshTimer=window.setTimeout(()=>{
      refreshData();
    },2500);

    const noticeTimer=window.setTimeout(()=>{
      setShowPaymentNotice(false);
    },10000);

    return ()=>{
      window.clearTimeout(refreshTimer);
      window.clearTimeout(noticeTimer);
    };
  },[paymentResult,user,refreshData]);

  useEffect(()=>{
    if (!linkedOrderId || !user || !orders.length) return;
    if (linkedOrderRef.current===linkedOrderId) return;

    const linkedOrder=orders.find((order)=>order.id===linkedOrderId);
    if (!linkedOrder) return;

    const isSellingOrder=currentSeller?.id===linkedOrder.seller_id;
    setActiveRoleTab(isSellingOrder ? 'selling' : 'buying');
    setExpandedOrder(linkedOrder.id);
    linkedOrderRef.current=linkedOrderId;
  },[linkedOrderId,user,orders,currentSeller?.id]);

  const displayOrders=useMemo(
    ()=>filterAndSortOrders(
      orders,
      {
        search,
        status:filter,
        sort,
        role:activeRoleTab,
        userId:user?.id,
        sellerId:currentSeller?.id,
        hideCancelled
      }
    ),
    [
      orders,
      search,
      filter,
      sort,
      activeRoleTab,
      user?.id,
      currentSeller?.id,
      hideCancelled
    ]
  );

  const pageCount=Math.max(1,Math.ceil(displayOrders.length/ORDERS_PER_PAGE));
  const pageOrders=displayOrders.slice(
    (page-1)*ORDERS_PER_PAGE,
    page*ORDERS_PER_PAGE
  );

  useEffect(()=>{
    setPage(1);
  },[activeRoleTab,filter,sort,search,hideCancelled]);

  useEffect(()=>{
    if (page>pageCount) setPage(pageCount);
  },[page,pageCount]);

  useEffect(()=>{
    if (!linkedOrderId || !displayOrders.length) return;
    const linkedOrderIndex=displayOrders.findIndex(
      (order)=>order.id===linkedOrderId
    );

    if (linkedOrderIndex>=0) {
      setPage(Math.floor(linkedOrderIndex/ORDERS_PER_PAGE)+1);
    }
  },[linkedOrderId,displayOrders]);

  useEffect(()=>{
    if (
      isDataReady
      && expandedOrder
      && !displayOrders.some((order)=>order.id===expandedOrder)
    ) {
      setExpandedOrder(null);
    }
  },[isDataReady,displayOrders,expandedOrder]);

  const handleCancelOrder=async(event,orderId)=>{
    event.stopPropagation();

    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    setCancellingId(orderId);

    try {
      await updateOrderStatus(orderId,'cancelled');
    } catch (error) {
      window.alert(error.message || 'Failed to update order.');
    } finally {
      setCancellingId(null);
    }
  };

  const handleStatusUpdate=async(event,orderId,status)=>{
    event.stopPropagation();

    if (updatingOrderId===orderId) return;

    setExpandedOrder(orderId);
    setUpdatingOrderId(orderId);

    try {
      await updateOrderStatus(orderId,status);
    } catch (error) {
      window.alert(error.message || 'Failed to update order status.');
    } finally {
      setUpdatingOrderId(null);
      setExpandedOrder(orderId);
    }
  };

  const handleMessage=(order)=>{
    const targetId=activeRoleTab==='selling'
      ? order.buyer_id
      : order.seller?.user_id;

    if (targetId) navigate(`/chat/${targetId}`);
  };

  const handlePageChange=(nextPage)=>{
    setPage(nextPage);
    setExpandedOrder(null);
    window.scrollTo({top:0,behavior:'smooth'});
  };

  if (!user) {
    return(
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <SafeIcon
            icon={FiShoppingBag}
            className="mx-auto mb-6 text-6xl text-slate-200"
          />
          <h2 className="text-2xl font-black uppercase tracking-widest text-slate-900">
            Sign in to view orders
          </h2>
        </div>
      </div>
    );
  }

  return(
    <div className="min-h-screen bg-slate-50 py-16">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <h1 className="mb-2 text-4xl font-black tracking-tighter text-slate-900">
              My <span className="text-indigo-600">Orders</span>
            </h1>
            <p className="font-medium text-slate-500">
              Showing {displayOrders.length} order{displayOrders.length===1 ? '' : 's'}
            </p>
          </div>

          {isSeller && (
            <div className="flex rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm">
              <button
                type="button"
                onClick={()=>setActiveRoleTab('buying')}
                className={`flex items-center rounded-xl px-6 py-2.5 text-xs font-black uppercase tracking-widest ${activeRoleTab==='buying' ? 'bg-indigo-600 text-white' : 'text-slate-400'}`}
              >
                <SafeIcon icon={FiShoppingCart} className="mr-2" />
                Purchases
              </button>

              <button
                type="button"
                onClick={()=>setActiveRoleTab('selling')}
                className={`flex items-center rounded-xl px-6 py-2.5 text-xs font-black uppercase tracking-widest ${activeRoleTab==='selling' ? 'bg-indigo-600 text-white' : 'text-slate-400'}`}
              >
                <SafeIcon icon={FiPackage} className="mr-2" />
                Sales
              </button>
            </div>
          )}
        </div>

        {paymentResult==='success' && showPaymentNotice && (
          <div className="mb-8 flex items-start gap-4 rounded-3xl border border-emerald-200 bg-emerald-50 p-6 text-emerald-800 shadow-sm">
            <SafeIcon
              icon={FiCheckCircle}
              className="mt-0.5 flex-shrink-0 text-2xl text-emerald-600"
            />
            <div>
              <h2 className="text-sm font-black uppercase tracking-widest">
                Order placed successfully
              </h2>
              <p className="mt-2 text-sm font-medium leading-relaxed">
                Your payment method was securely authorized for order{' '}
                <span className="font-black">
                  {paymentOrderId
                    ? `#${paymentOrderId.slice(0,8).toUpperCase()}`
                    : 'confirmation'}
                </span>
                . The seller must accept the order before the payment is captured.
                You can track the order status below.
              </p>
            </div>
          </div>
        )}

        {paymentResult==='cancelled' && showPaymentNotice && (
          <div className="mb-8 flex items-start gap-4 rounded-3xl border border-amber-200 bg-amber-50 p-6 text-amber-800 shadow-sm">
            <SafeIcon
              icon={FiAlertCircle}
              className="mt-0.5 flex-shrink-0 text-2xl text-amber-600"
            />
            <div>
              <h2 className="text-sm font-black uppercase tracking-widest">
                Checkout was cancelled
              </h2>
              <p className="mt-2 text-sm font-medium leading-relaxed">
                Your order request was saved, but checkout was not completed.
                No payment was captured. Open the order below or contact the seller
                if you need help.
              </p>
            </div>
          </div>
        )}

        <div className="mb-10 space-y-4">
          <div className="relative">
            <SafeIcon
              icon={FiSearch}
              className="absolute left-5 top-1/2 -translate-y-1/2 text-xl text-slate-300"
            />
            <input
              type="search"
              value={search}
              onChange={(event)=>setSearch(event.target.value)}
              placeholder="Search orders,items,sellers,or order IDs..."
              className="w-full rounded-2xl border border-slate-100 bg-white py-5 pl-14 pr-6 font-bold outline-none"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-100 bg-white p-2 shadow-sm">
            <SafeIcon icon={FiFilter} className="ml-2 text-slate-300" />

            {STATUSES.map((status)=>(
              <button
                key={status}
                type="button"
                onClick={()=>setFilter(status)}
                className={`rounded-xl px-4 py-2.5 text-[10px] font-black uppercase tracking-widest ${filter===status ? 'bg-slate-900 text-white' : 'text-slate-400'}`}
              >
                {status}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <label className="flex w-fit cursor-pointer items-center gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
              <input
                type="checkbox"
                checked={hideCancelled}
                onChange={(event)=>setHideCancelled(event.target.checked)}
                className="h-4 w-4 accent-indigo-600"
              />
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                Hide cancelled orders
              </span>
            </label>

            <label className="flex w-fit items-center gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
              <SafeIcon icon={FiArrowUpDown} className="text-indigo-500" />
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                Arrange
              </span>

              <select
                value={sort}
                onChange={(event)=>setSort(event.target.value)}
                className="bg-transparent text-xs font-black text-slate-700 outline-none"
              >
                {ORDER_SORT_OPTIONS.map((option)=>(
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="space-y-6">
          {displayOrders.length>0 ? (
            pageOrders.map((order)=>(
              <OrderCard
                key={order.id}
                order={order}
                isSelling={activeRoleTab==='selling'}
                expanded={expandedOrder===order.id}
                onToggle={()=>setExpandedOrder(
                  expandedOrder===order.id ? null : order.id
                )}
                onCancel={handleCancelOrder}
                cancellingId={cancellingId}
                onReview={setOrderToReview}
                onPrint={setOrderToPrint}
                onMessage={handleMessage}
                onStatusUpdate={handleStatusUpdate}
                updatingOrderId={updatingOrderId}
                onPickupConfirmed={()=>setExpandedOrder(null)}
                onDeliveryConfirmed={()=>setExpandedOrder(null)}
                onAutoConfirmed={refreshData}
                onDisputeReported={refreshData}
              />
            ))
          ) : (
            <div className="rounded-[3rem] border border-dashed border-slate-200 bg-white py-32 text-center">
              <SafeIcon
                icon={search || filter!=='all' ? FiSearch : FiShoppingBag}
                className="mx-auto mb-8 text-4xl text-slate-200"
              />

              <h3 className="mb-2 text-2xl font-black text-slate-900">
                {search || filter!=='all'
                  ? 'No matching orders'
                  : 'No orders found'}
              </h3>

              <p className="font-medium tracking-wide text-slate-500">
                {search || filter!=='all'
                  ? 'Try changing your search or filter.'
                  : 'Start exploring the marketplace to find something delicious!'}
              </p>
            </div>
          )}
        </div>

        {displayOrders.length>ORDERS_PER_PAGE && (
          <nav
            aria-label="Orders pages"
            className="mt-8 flex flex-wrap items-center justify-center gap-2"
          >
            <button
              type="button"
              onClick={()=>handlePageChange(page-1)}
              disabled={page===1}
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-600 transition-colors hover:border-indigo-200 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <SafeIcon icon={FiChevronLeft} />
              Previous
            </button>

            {Array.from({length:pageCount},(_,index)=>index+1).map((pageNumber)=>(
              <button
                key={pageNumber}
                type="button"
                onClick={()=>handlePageChange(pageNumber)}
                aria-current={page===pageNumber ? 'page' : undefined}
                className={`h-11 min-w-11 rounded-xl px-3 text-xs font-black transition-colors ${page===pageNumber ? 'bg-indigo-600 text-white' : 'border border-slate-200 bg-white text-slate-600 hover:border-indigo-200 hover:text-indigo-600'}`}
              >
                {pageNumber}
              </button>
            ))}

            <button
              type="button"
              onClick={()=>handlePageChange(page+1)}
              disabled={page===pageCount}
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-600 transition-colors hover:border-indigo-200 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
              <SafeIcon icon={FiChevronRight} />
            </button>
          </nav>
        )}
      </div>

      {orderToReview && (
        <ReviewModal
          order={orderToReview}
          onClose={()=>setOrderToReview(null)}
          onSubmit={addReview}
        />
      )}

      {orderToPrint && (
        <InvoiceDocument
          order={orderToPrint}
          onClose={()=>setOrderToPrint(null)}
        />
      )}
    </div>
  );
};

export default OrdersPage;
